import '../scss/components/_ownerreports.scss'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { get_occupancy_status } from '../apis/services/pgApi'
import { getAllComplaintsByPGID } from '../apis/services/pgApi'

// ─── Constants ───────────────────────────────────────────────────────────────

const REPORT_TYPES = [
  { key: 'occupancy',  label: 'Occupancy Report'  },
  { key: 'revenue',    label: 'Revenue Report'     },
  { key: 'complaints', label: 'Complaints Report'  },
  { key: 'compliance', label: 'Compliance Report'  },
]

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

const MOCK_MONTHLY = {
  2023: [74, 71, 77, 80, 76, 82, 87, 85, 83, 80, 77, 75],
  2024: [80, 83, 86, 89, 85, 90, 93, 91, 88, 87, 83, 81],
  2025: [84, 87, 90, 92, 89, 94, 97, 95, 92, 90, 86, 85],
  2026: [86, 88, 91, 93, 90, 95, null, null, null, null, null, null],
}

// ─── Complaints constants ────────────────────────────────────────────────────

const TYPE_ICON = {
  'Water Leakage':'🚿','WiFi Issue':'📶','AC Not Working':'❄️','Noisy Neighbours':'🔊',
  'Housekeeping':'🧹','Door Lock':'🔒','Plumbing':'🔧','Electricity':'⚡','Security':'🛡️',
  'Pest Control':'🪲','Furniture':'🪑','Geyser/Hot Water':'🚿','Fan':'💨',
  'Drainage':'🚰','Paint/Walls':'🎨','Cleanliness':'🧼','Laundry':'👕','Parking':'🚗',
  'Noise':'📢','CCTV/Safety':'📷','Lift/Elevator':'🛗','Gas/Cooking':'🔥',
  'Window/Door':'🪟','Mattress/Bedding':'🛏️','Food Quality':'🍽️',
}

const HIGH_PRI   = ['Water Leakage','Plumbing','Electricity','AC Not Working','Door Lock','Geyser/Hot Water','Security','Gas/Cooking','Lift/Elevator','CCTV/Safety']
const MOD_PRI    = ['WiFi Issue','Housekeeping','Furniture','Pest Control','Window/Door','Drainage','Paint/Walls']

function getPriority(type) {
  if (HIGH_PRI.includes(type)) return 'high'
  if (MOD_PRI.includes(type))  return 'moderate'
  return 'low'
}

function mapStatus(s) {
  const l = (s || '').toLowerCase()
  if (l === 'resolved') return 'resolved'
  if (l === 'rejected') return 'rejected'
  return 'open'
}

function formatDate(str) {
  if (!str) return '—'
  return new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── Shared helpers ──────────────────────────────────────────────────────────

function getHealthSummary(avg) {
  if (avg >= 95) return { text: 'Outstanding occupancy', tag: 'excellent' }
  if (avg >= 90) return { text: 'Excellent occupancy',   tag: 'excellent' }
  if (avg >= 85) return { text: 'Healthy occupancy',     tag: 'healthy'   }
  if (avg >= 80) return { text: 'Needs improvement',     tag: 'needs-improvement' }
  return           { text: 'Attention required',         tag: 'attention' }
}

// ─── Shared subcomponents ────────────────────────────────────────────────────

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rpt-stat-tile" style={{ borderTopColor: accent }}>
      <span className="rpt-stat-value" style={{ color: accent }}>{value}</span>
      <span className="rpt-stat-label">{label}</span>
      {sub && <span className="rpt-stat-sub">{sub}</span>}
    </div>
  )
}

function ComingSoon({ label }) {
  return (
    <div className="rpt-coming-soon">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.4">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 9h6M9 12h6M9 15h4" />
      </svg>
      <p className="rpt-coming-title">{label} coming soon</p>
      <p className="rpt-coming-sub">This report will appear here once connected to its data source.</p>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// OCCUPANCY REPORT  (your existing code — untouched)
// ═══════════════════════════════════════════════════════════════════════════════

function MonthlyOccupancyReport({ selectedPGId, selectedPG, dateFrom, dateTo, selectedYear, onYearChange }) {
  const [bedData, setBedData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [target,  setTarget]  = useState(85)
  const [showYoY, setShowYoY] = useState(false)

  const { year: currentYear, month: currentMonth } = useMemo(() => {
    const d = new Date(dateTo)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
  }, [dateTo])

  useEffect(() => {
    if (!selectedPGId) return
    setLoading(true)
    setError(null)
    get_occupancy_status(selectedPGId)
      .then(response => {
        let beds = []
        if (Array.isArray(response)) {
          beds = response.length >= 1 ? response[0] : response
        } else if (response?.data) {
          beds = response.data
        } else {
          beds = response || []
        }
        setBedData(beds)
      })
      .catch(err => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false))
  }, [selectedPGId])

  const bedStats = useMemo(() => {
    const total    = bedData.length
    const occupied = bedData.filter(b => (b.OccupancyStatus || '').toUpperCase() === 'OCCUPIED').length
    const notice   = bedData.filter(b => (b.OccupancyStatus || '').toUpperCase() === 'NOTICE').length
    const vacant   = total - occupied - notice
    const rate     = total ? ((occupied / total) * 100) : 0
    return { total, occupied, notice, vacant, rate: Math.round(rate * 10) / 10 }
  }, [bedData])

  const monthlyData  = useMemo(() => MOCK_MONTHLY, [])
  const rawYearData  = monthlyData[selectedYear] || Array(12).fill(null)
  const maxMonth     = (selectedYear === currentYear) ? currentMonth : 12
  const yearData     = rawYearData.map((val, idx) => (idx + 1 > maxMonth ? null : val))

  const validData  = yearData.filter(v => v !== null && v !== undefined)
  const currentAvg = validData.length > 0 ? validData.reduce((a, b) => a + b, 0) / validData.length : 0
  const prevYearData = monthlyData[selectedYear - 1] || Array(12).fill(null)
  const prevValid    = prevYearData.filter(v => v !== null && v !== undefined)
  const prevAvg      = prevValid.length > 0 ? prevValid.reduce((a, b) => a + b, 0) / prevValid.length : 0
  const vsPrev       = currentAvg - prevAvg

  const peakVal    = validData.length > 0 ? Math.max(...validData) : 0
  const peakIdx    = yearData.indexOf(peakVal)
  const peakMonth  = peakIdx >= 0 ? MONTHS[peakIdx] : '—'
  const lowestVal  = validData.length > 0 ? Math.min(...validData) : 0
  const lowestIdx  = yearData.indexOf(lowestVal)
  const lowestMonth = lowestIdx >= 0 ? MONTHS[lowestIdx] : '—'

  const health    = getHealthSummary(currentAvg)
  const healthTag = health.tag

  const getBarClass = (val) => {
    if (val === null || val === undefined) return 'rpt-bar-future'
    return val >= target ? 'rpt-bar-met' : 'rpt-bar-missed'
  }

  const yoyYears   = [selectedYear - 2, selectedYear - 1, selectedYear]
  const yoyData    = yoyYears.map(y => monthlyData[y] || Array(12).fill(null))
  const availableYears = Object.keys(monthlyData).map(Number).sort((a, b) => a - b)

  if (loading) return <div className="rpt-state">Loading occupancy data…</div>
  if (error)   return <div className="rpt-state rpt-state-error">Error: {error}</div>

  return (
    <div className="rpt-section">

      <div className="rpt-tiles">
        <StatTile label="Total Beds"    value={bedStats.total}    accent="#6366F1" />
        <StatTile label="Occupied"      value={bedStats.occupied} sub={`${bedStats.rate}% fill rate`} accent="#22C55E" />
        <StatTile label="Notice Period" value={bedStats.notice}   sub="Moving out soon" accent="#F59E0B" />
        <StatTile label="Vacant"        value={bedStats.vacant}   sub="Available now"   accent="#EF4444" />
      </div>

      <div className="rpt-chart-grid">
        <div className="rpt-chart-card">
          <div className="rpt-chart-title">
            <span>Monthly Occupancy • {selectedYear}</span>
            <span className="sub">Target <strong>{target}%</strong></span>
          </div>
          <div className="rpt-legend">
            <span><span className="rpt-legend-dot met"></span> Met target</span>
            <span><span className="rpt-legend-dot missed"></span> Below target</span>
            <span><span className="rpt-legend-dot future"></span> No data yet</span>
          </div>
          <div className="rpt-barchart">
            {yearData.map((val, i) => {
              const isFuture = val === null || val === undefined
              const isPeak   = !isFuture && val >= peakVal && val === peakVal
              return (
                <div key={i} className="rpt-bar-col">
                  <span className="rpt-bar-value">{isFuture ? '—' : Math.round(val) + '%'}</span>
                  <div
                    className={`rpt-bar ${getBarClass(val)}`}
                    style={{ height: isFuture ? '4px' : `${Math.max(4, (val / 100) * 100)}%` }}
                  >
                    {isPeak && <span className="rpt-bar-peak-marker">▲</span>}
                  </div>
                  <span className="rpt-bar-label">{MONTHS[i]}</span>
                </div>
              )
            })}
          </div>
          <div className="rpt-target-section">
            <div className="rpt-target-label">
              Occupancy Target
              <input
                type="number"
                className="rpt-target-input"
                value={target}
                onChange={e => setTarget(Math.min(100, Math.max(50, Number(e.target.value) || 85)))}
                min={50} max={100}
              />
            </div>
            <div className="rpt-target-meta">
              <span>Above: <strong className="above">{validData.filter(v => v >= target).length}/{validData.length}</strong></span>
              <span>Below: <strong className="below">{validData.filter(v => v < target).length}/{validData.length}</strong></span>
            </div>
          </div>
        </div>

        <div className="rpt-quarter-card">
          <div className="rpt-quarter-label">Best Quarter</div>
          <div className="rpt-quarter-value">
            {(() => {
              let bestQ = { label: '', avg: 0, range: '' }
              for (let q = 0; q < 4; q++) {
                const start = q * 3
                const slice = yearData.slice(start, start + 3).filter(v => v !== null && v !== undefined)
                if (slice.length === 0) continue
                const avg = slice.reduce((a, b) => a + b, 0) / slice.length
                if (avg > bestQ.avg) {
                  bestQ = { label: `Q${q + 1}`, avg: Math.round(avg * 10) / 10, range: `${MONTHS[start]}–${MONTHS[start + 2]}` }
                }
              }
              return bestQ.label || '—'
            })()}
          </div>
          <div className="rpt-quarter-sub">
            {(() => {
              let bestQ = { label: '', avg: 0, range: '' }
              for (let q = 0; q < 4; q++) {
                const start = q * 3
                const slice = yearData.slice(start, start + 3).filter(v => v !== null && v !== undefined)
                if (slice.length === 0) continue
                const avg = slice.reduce((a, b) => a + b, 0) / slice.length
                if (avg > bestQ.avg) {
                  bestQ = { label: `Q${q + 1}`, avg: Math.round(avg * 10) / 10, range: `${MONTHS[start]}–${MONTHS[start + 2]}` }
                }
              }
              return bestQ.range ? `${bestQ.range} · ${bestQ.avg}% avg` : 'No data'
            })()}
          </div>
        </div>
      </div>

      <div className="rpt-health-card">
        <div className="rpt-health-summary">
          Your PG is running at <span className="highlight">{currentAvg.toFixed(1)}%</span> average occupancy this year —
          <span className={`rpt-health-tag ${healthTag}`}>{health.text}</span>.
          {validData.length < 12 && ` (based on ${validData.length} months of data)`}
        </div>
        <div className="rpt-health-meta">
          <span className="rpt-health-meta-item">Peak month: <strong>{peakMonth} – {peakVal}%</strong></span>
          <span className="rpt-health-meta-item">Lowest month: <strong>{lowestMonth} – {lowestVal}%</strong></span>
          <span className="rpt-health-meta-item">Year average: <strong>{currentAvg.toFixed(1)}%</strong></span>
          <span className="rpt-health-meta-item">
            vs {selectedYear - 1}: <strong className={vsPrev >= 0 ? 'up' : 'down'}>
              {vsPrev >= 0 ? `+${vsPrev.toFixed(1)}%` : `${vsPrev.toFixed(1)}%`}
            </strong>
          </span>
        </div>
      </div>

      <div className="rpt-yoy-card">
        <div className="rpt-yoy-head">
          <h3 className="rpt-yoy-title">Year-on-Year Comparison</h3>
          <button className="rpt-yoy-toggle" onClick={() => setShowYoY(!showYoY)} aria-expanded={showYoY}>
            {showYoY ? 'Hide' : 'Show'} Years
            <span className={`rpt-chevron ${showYoY ? 'rpt-chevron-open' : ''}`}>▼</span>
          </button>
        </div>
        {showYoY && (
          <div className="rpt-yoy-wrap">
            <table className="rpt-yoy-table">
              <thead>
                <tr>
                  <th>Month</th>
                  {yoyYears.map(y => <th key={y}>{y}</th>)}
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {MONTHS.map((month, i) => {
                  const values  = yoyData.map(data => data[i] !== null && data[i] !== undefined ? data[i] : null)
                  const v1      = values[1]
                  const v2      = values[2]
                  const change  = (v1 !== null && v2 !== null) ? v2 - v1 : null
                  const cls     = change > 0 ? 'change-up' : change < 0 ? 'change-down' : 'change-neutral'
                  const chgStr  = change !== null ? (change > 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`) : '—'
                  return (
                    <tr key={i}>
                      <td>{month}</td>
                      {values.map((v, idx) => (
                        <td key={idx} className={idx === 2 && v !== null ? 'rpt-td-current' : ''}>
                          {v !== null ? `${v}%` : '—'}
                        </td>
                      ))}
                      <td className={change !== null ? cls : 'change-neutral'}>{chgStr}</td>
                    </tr>
                  )
                })}
                <tr className="rpt-avg-row">
                  <td>Average</td>
                  {yoyData.map((data, idx) => {
                    const valid = data.filter(v => v !== null && v !== undefined)
                    const avg   = valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : null
                    return <td key={idx}>{avg !== null ? avg.toFixed(1) + '%' : '—'}</td>
                  })}
                  <td>
                    {(() => {
                      const pv = yoyData[1].filter(v => v !== null && v !== undefined)
                      const cv = yoyData[2].filter(v => v !== null && v !== undefined)
                      if (!pv.length || !cv.length) return '—'
                      const diff = (cv.reduce((a,b)=>a+b,0)/cv.length) - (pv.reduce((a,b)=>a+b,0)/pv.length)
                      return (
                        <span className={diff > 0 ? 'change-up' : diff < 0 ? 'change-down' : 'change-neutral'}>
                          {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
                        </span>
                      )
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPLAINTS REPORT
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Rich dummy data — purposeful patterns for stakeholder demos ─────────────
//
// Patterns baked in so the report tells a real story:
//   • WiFi Issue + Housekeeping are the top recurring problems every year
//   • AC Not Working spikes every summer (May–Aug)
//   • Water Leakage / Plumbing cluster in monsoon (Jul–Sep)
//   • Resolution rate improves year-over-year: 2023→68%, 2024→74%, 2025→82%, 2026→88%
//   • Complaint volume edges up slightly each year (more tenants = more noise)
//   • 2026 has data only through June (current year)
//
const DUMMY_COMPLAINTS = (() => {
  const tenants = [
    'Rahul Sharma','Sneha Reddy','Arjun Choudhary','Pooja Gupta','Meena Dasari',
    'Vikas Rao','Kiran Menon','Deepak Joshi','Ananya Singh','Suresh Iyer',
    'Priya Nair','Naveen Pillai','Sonia Agarwal','Ravi Kumar','Amit Verma',
  ]
  const rooms = ['101','102','103','201','202','203','204','301','302','303','304']

  // Issue pool with weights per month — higher weight = more likely that month
  // Format: [type, baseWeight, monthBoosts: {monthIndex: extraWeight}]
  const ISSUE_POOL = [
    ['WiFi Issue',        9,  {}],
    ['Housekeeping',      8,  {}],
    ['AC Not Working',    5,  { 4:8, 5:10, 6:10, 7:10, 8:7 }],   // May–Sep spike
    ['Water Leakage',     4,  { 6:6, 7:8, 8:6 }],                  // monsoon
    ['Plumbing',          4,  { 6:5, 7:7, 8:5 }],                  // monsoon
    ['Noisy Neighbours',  4,  {}],
    ['Electricity',       3,  {}],
    ['Fan',               3,  { 3:3, 4:5, 5:4 }],                  // summer warmup
    ['Cleanliness',       3,  {}],
    ['Pest Control',      3,  { 5:4, 6:5, 7:4 }],                  // pre/post monsoon
    ['Furniture',         2,  {}],
    ['Door Lock',         2,  {}],
    ['Geyser/Hot Water',  2,  { 10:4, 11:5, 0:5, 1:4 }],          // winter
    ['Drainage',          2,  { 6:4, 7:5, 8:3 }],
    ['Laundry',           2,  {}],
    ['Parking',           1,  {}],
    ['Paint/Walls',       1,  {}],
    ['Security',          1,  {}],
    ['Mattress/Bedding',  1,  {}],
    ['Food Quality',      1,  {}],
  ]

  function pickType(monthIdx) {
    const pool = []
    ISSUE_POOL.forEach(([type, base, boosts]) => {
      const w = base + (boosts[monthIdx] || 0)
      for (let i = 0; i < w; i++) pool.push(type)
    })
    return pool[Math.floor(Math.random() * pool.length)]
  }

  // Resolution probability improves each year
  const RES_PROB = { 2023: 0.68, 2024: 0.74, 2025: 0.82, 2026: 0.88 }

  // Base complaints per month, slight growth each year
  const BASE_PER_MONTH = { 2023: 8, 2024: 10, 2025: 12, 2026: 13 }

  // Seasonal volume modifier
  const SEASONAL = [0.8, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.2, 1.0, 0.9, 0.85, 0.8]

  let id = 1
  const out = []

  ;[2023, 2024, 2025, 2026].forEach(yr => {
    const maxMonth = yr === 2026 ? 5 : 11   // 2026: Jan–Jun only
    const resPr    = RES_PROB[yr]
    const base     = BASE_PER_MONTH[yr]

    for (let mi = 0; mi <= maxMonth; mi++) {
      const n = Math.round(base * SEASONAL[mi] + (Math.random() * 3 - 1.5))
      for (let j = 0; j < n; j++) {
        const type   = pickType(mi)
        const day    = 1 + Math.floor(Math.random() * 27)
        const isRes  = Math.random() < resPr
        // A few rejections (~5% of resolved)
        const status = isRes ? (Math.random() < 0.05 ? 'REJECTED' : 'RESOLVED') : 'Open'
        out.push({
          ComplaintID:     id++,
          ComplaintText:   type,
          ComplaintStatus: status,
          ComplaintDate:   new Date(yr, mi, day).toISOString(),
          tenantName:      tenants[Math.floor(Math.random() * tenants.length)],
          RoomNumber:      rooms[Math.floor(Math.random() * rooms.length)],
          _year:  String(yr),
          _month: mi,
        })
      }
    }
  })

  return out
})()

// ── Sub-components ──────────────────────────────────────────────────────────

function PriBadge({ type }) {
  const p = getPriority(type)
  const cls = { high: 'cp-rpt-pri-high', moderate: 'cp-rpt-pri-mod', low: 'cp-rpt-pri-low' }[p]
  const lbl = { high: 'High', moderate: 'Moderate', low: 'Low' }[p]
  return <span className={`cp-rpt-pri ${cls}`}>{lbl}</span>
}

function StatusBadge({ status }) {
  const s   = mapStatus(status)
  const cls = { open: 'cp-rpt-st-open', resolved: 'cp-rpt-st-res', rejected: 'cp-rpt-st-rej' }[s]
  const lbl = { open: 'Open', resolved: 'Resolved', rejected: 'Rejected' }[s]
  const dot = { open: '#3B82F6', resolved: '#22C55E', rejected: '#EF4444' }[s]
  return (
    <span className={`cp-rpt-status ${cls}`}>
      <span className="cp-rpt-dot" style={{ background: dot }} />{lbl}
    </span>
  )
}

// ── Overview tab ─────────────────────────────────────────────────────────────

function CpOverview({ data, selectedYear }) {
  const all      = data
  const open     = all.filter(c => mapStatus(c.ComplaintStatus) === 'open')
  const resolved = all.filter(c => mapStatus(c.ComplaintStatus) === 'resolved')
  const highOpen = open.filter(c => getPriority(c.ComplaintText) === 'high')
  const resRate  = all.length ? Math.round((resolved.length / all.length) * 100) : 0

  // Verdict
  let verdict, verdictCls
  if (highOpen.length === 0 && open.length < 5) {
    verdict    = `Your PG is in good shape — only ${open.length} open complaint${open.length !== 1 ? 's' : ''} and no high-priority issues pending.`
    verdictCls = 'cp-rpt-verdict-good'
  } else if (highOpen.length > 0) {
    verdict    = `${highOpen.length} high-priority complaint${highOpen.length !== 1 ? 's are' : ' is'} open — things like plumbing, electricity, or water issues tenants expect fixed fast.`
    verdictCls = 'cp-rpt-verdict-bad'
  } else {
    verdict    = `${open.length} complaints are open. None are high priority, but clearing the backlog keeps tenants happy.`
    verdictCls = 'cp-rpt-verdict-ok'
  }

  // Top recurring types (all time)
  const typeCounts = {}
  all.forEach(c => { typeCounts[c.ComplaintText] = (typeCounts[c.ComplaintText] || 0) + 1 })
  const topTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).slice(0, 7)
  const maxCount = topTypes[0]?.[1] || 1

  // Monthly volume for selected year
  const monthlyVol = MONTHS.map((_, mi) => data.filter(c => c._year === selectedYear && c._month === mi).length)

  const priColor = { high: '#EF4444', moderate: '#F59E0B', low: '#22C55E' }

  return (
    <div className="cp-rpt-section">

      {/* KPI tiles */}
      <div className="rpt-tiles">
        <StatTile label="Open complaints"      value={open.length}     sub={highOpen.length > 0 ? `${highOpen.length} high priority` : 'None high priority'} accent="#EF4444" />
        <StatTile label="Resolution rate"      value={`${resRate}%`}   sub={`${resolved.length} of ${all.length} resolved`} accent="#22C55E" />
        <StatTile label="Total complaints"     value={all.length}      sub="All time" accent="#6366F1" />
        <StatTile label="High priority open"   value={highOpen.length} sub="Needs immediate action" accent="#F59E0B" />
      </div>

      {/* Verdict card */}
      <div className={`cp-rpt-verdict-card ${verdictCls}`}>
        <p className="cp-rpt-verdict-text">{verdict}</p>
        <div className="cp-rpt-verdict-pills">
          <span className="cp-rpt-vpill cp-rpt-vpill-red">
            <span>🔴</span><span className="vpl">High priority open</span><strong>{highOpen.length}</strong>
          </span>
          <span className="cp-rpt-vpill cp-rpt-vpill-green">
            <span>✓</span><span className="vpl">Resolution rate</span><strong>{resRate}%</strong>
          </span>
          <span className="cp-rpt-vpill cp-rpt-vpill-blue">
            <span>📅</span><span className="vpl">This year ({selectedYear})</span>
            <strong>{data.filter(c => c._year === selectedYear).length} total</strong>
          </span>
          <span className="cp-rpt-vpill cp-rpt-vpill-gray">
            <span>📌</span><span className="vpl">Most reported issue</span><strong>{topTypes[0]?.[0] || '—'}</strong>
          </span>
        </div>
      </div>

      {/* Two-col: recurring issues + monthly sparkbar */}
      <div className="cp-rpt-two-col">

        <div className="cp-rpt-card">
          <div className="cp-rpt-card-title">Top recurring issues <span className="cp-rpt-card-sub">all time</span></div>
          <div className="cp-rpt-issue-rows">
            {topTypes.map(([type, count]) => {
              const pri  = getPriority(type)
              const barW = Math.round((count / maxCount) * 100)
              return (
                <div key={type} className="cp-rpt-issue-row">
                  <span className="cp-rpt-issue-icon">{TYPE_ICON[type] || '📌'}</span>
                  <span className="cp-rpt-issue-name">{type}</span>
                  <div className="cp-rpt-issue-track">
                    <div className="cp-rpt-issue-bar" style={{ width: `${barW}%`, background: priColor[pri] }} />
                  </div>
                  <span className="cp-rpt-issue-count">{count}</span>
                  <PriBadge type={type} />
                </div>
              )
            })}
          </div>
          <p className="cp-rpt-card-hint">Bar length = frequency · colour = priority level</p>
        </div>

        <div className="cp-rpt-card">
          <div className="cp-rpt-card-title">Monthly volume <span className="cp-rpt-card-sub">{selectedYear}</span></div>
          <div className="cp-rpt-sparkbars">
            {monthlyVol.map((n, i) => {
              const maxV = Math.max(...monthlyVol, 1)
              const h    = Math.max(4, Math.round((n / maxV) * 100))
              return (
                <div key={i} className="cp-rpt-spark-col">
                  <span className="cp-rpt-spark-val">{n}</span>
                  <div className="cp-rpt-spark-bar" style={{ height: `${h}%` }} />
                  <span className="cp-rpt-spark-lbl">{MONTHS[i]}</span>
                </div>
              )
            })}
          </div>
          <p className="cp-rpt-card-hint">Number of complaints raised each month</p>
        </div>

      </div>
    </div>
  )
}

// ── Monthly trends tab ───────────────────────────────────────────────────────

function CpTrends({ data }) {
  const years      = ['2023','2024','2025','2026'].filter(y => data.some(c => c._year === y))
  const yearColors = { '2023':'#B4B2A9','2024':'#85B7EB','2025':'#F06E18','2026':'#22C55E' }

  const mthRows = MONTHS.map((m, mi) => {
    const vals = years.map(y => data.filter(c => c._year === y && c._month === mi).length)
    const last = vals[vals.length - 1]
    const prev = vals[vals.length - 2]
    const diff = prev !== undefined ? last - prev : null
    return { month: m, vals, diff }
  })

  const totals    = years.map(y => data.filter(c => c._year === y).length)
  const resRates  = years.map(y => {
    const yc  = data.filter(c => c._year === y)
    return yc.length ? Math.round((yc.filter(c => mapStatus(c.ComplaintStatus) === 'resolved').length / yc.length) * 100) : 0
  })

  const latestY = years[years.length - 1]
  const prevY   = years[years.length - 2]
  const totalDiff = prevY ? totals[years.length - 1] - totals[years.length - 2] : null

  return (
    <div className="cp-rpt-section">

      {/* Summary banner */}
      <div className="cp-rpt-trends-banner">
        <div className="cp-rpt-tbanner-item">
          <span className="cp-rpt-tbanner-lbl">Total in {latestY}</span>
          <span className="cp-rpt-tbanner-val">{totals[years.length - 1]}</span>
        </div>
        {totalDiff !== null && (
          <div className="cp-rpt-tbanner-item">
            <span className="cp-rpt-tbanner-lbl">vs {prevY}</span>
            <span className={`cp-rpt-tbanner-val ${totalDiff > 0 ? 'cp-rpt-up' : 'cp-rpt-dn'}`}>
              {totalDiff > 0 ? `+${totalDiff}` : totalDiff} complaints
            </span>
          </div>
        )}
        <div className="cp-rpt-tbanner-item">
          <span className="cp-rpt-tbanner-lbl">Resolution rate {latestY}</span>
          <span className="cp-rpt-tbanner-val cp-rpt-dn-good">{resRates[years.length - 1]}%</span>
        </div>
      </div>

      {/* Month-by-month table */}
      <div className="cp-rpt-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="cp-rpt-tbl-head">
          <span className="cp-rpt-card-title" style={{ margin: 0 }}>Month-by-month breakdown</span>
          <div className="cp-rpt-yr-legend">
            {years.map(y => (
              <span key={y} className="cp-rpt-yr-leg-item">
                <span className="cp-rpt-yr-dot" style={{ background: yearColors[y] }} />{y}
              </span>
            ))}
          </div>
        </div>
        <div className="cp-rpt-tbl-scroll">
          <table className="cp-rpt-table">
            <thead>
              <tr>
                <th>Month</th>
                {years.map(y => <th key={y} style={{ color: yearColors[y] }}>{y}</th>)}
                <th>{prevY}→{latestY}</th>
              </tr>
            </thead>
            <tbody>
              {mthRows.map((row, i) => (
                <tr key={i}>
                  <td className="cp-rpt-td-bold">{row.month}</td>
                  {row.vals.map((v, vi) => <td key={vi}>{v}</td>)}
                  <td>
                    {row.diff === null ? '—'
                      : row.diff > 0
                        ? <span className="cp-rpt-diff-up">+{row.diff}</span>
                        : row.diff < 0
                          ? <span className="cp-rpt-diff-dn">{row.diff}</span>
                          : <span className="cp-rpt-diff-eq">—</span>
                    }
                  </td>
                </tr>
              ))}
              <tr className="cp-rpt-total-row">
                <td className="cp-rpt-td-bold">Total</td>
                {totals.map((t, i) => <td key={i}><strong>{t}</strong></td>)}
                <td>
                  {totalDiff !== null
                    ? totalDiff > 0
                      ? <span className="cp-rpt-diff-up">+{totalDiff}</span>
                      : <span className="cp-rpt-diff-dn">{totalDiff}</span>
                    : '—'
                  }
                </td>
              </tr>
              <tr>
                <td className="cp-rpt-td-bold">Resolved %</td>
                {resRates.map((r, i) => (
                  <td key={i}>
                    {r}%
                    <span className="cp-rpt-res-bar">
                      <span className="cp-rpt-res-fill" style={{ width: `${r}%` }} />
                    </span>
                  </td>
                ))}
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  )
}

// ── All complaints tab ───────────────────────────────────────────────────────

function CpAllComplaints({ data }) {
  const [statusF, setStatusF] = useState('all')
  const [priF,    setPriF]    = useState('all')
  const [sortCol, setSortCol] = useState('date')
  const [sortDir, setSortDir] = useState('desc')

  const filtered = data.filter(c => {
    const s = mapStatus(c.ComplaintStatus)
    const p = getPriority(c.ComplaintText)
    return (statusF === 'all' || s === statusF) && (priF === 'all' || p === priF)
  })

  const sorted = [...filtered].sort((a, b) => {
    let av, bv
    if (sortCol === 'date')   { av = new Date(a.ComplaintDate); bv = new Date(b.ComplaintDate) }
    if (sortCol === 'type')   { av = a.ComplaintText;   bv = b.ComplaintText }
    if (sortCol === 'room')   { av = a.RoomNumber;       bv = b.RoomNumber }
    if (sortCol === 'tenant') { av = a.tenantName;       bv = b.tenantName }
    if (sortCol === 'status') { av = mapStatus(a.ComplaintStatus); bv = mapStatus(b.ComplaintStatus) }
    if (sortCol === 'pri')    { av = getPriority(a.ComplaintText); bv = getPriority(b.ComplaintText) }
    if (av < bv) return sortDir === 'asc' ? -1 : 1
    if (av > bv) return sortDir === 'asc' ? 1  : -1
    return 0
  })

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const SI = ({ col }) => (
    <span className={`cp-rpt-si${sortCol === col ? ' cp-rpt-si-on' : ''}`}>
      {sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
    </span>
  )

  return (
    <div className="cp-rpt-section">
      <div className="cp-rpt-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="cp-rpt-tbl-head">
          <span className="cp-rpt-card-title" style={{ margin: 0 }}>All complaints</span>
          <div className="cp-rpt-filter-row">
            <div className="cp-rpt-filter-group">
              {['all','open','resolved'].map(f => (
                <button key={f} className={`cp-rpt-fpill${statusF === f ? ' on' : ''}`} onClick={() => setStatusF(f)}>
                  {f === 'all' ? 'All status' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <div className="cp-rpt-filter-group">
              {['all','high','moderate','low'].map(f => (
                <button key={f} className={`cp-rpt-fpill${priF === f ? ' on' : ''}`} onClick={() => setPriF(f)}>
                  {f === 'all' ? 'All priority' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="cp-rpt-tbl-scroll">
          <table className="cp-rpt-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('type')}>Issue<SI col="type" /></th>
                <th onClick={() => handleSort('room')}>Room<SI col="room" /></th>
                <th onClick={() => handleSort('tenant')}>Tenant<SI col="tenant" /></th>
                <th onClick={() => handleSort('pri')}>Priority<SI col="pri" /></th>
                <th onClick={() => handleSort('date')}>Date<SI col="date" /></th>
                <th onClick={() => handleSort('status')}>Status<SI col="status" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr><td colSpan={6} className="cp-rpt-empty-row">No complaints match the selected filters.</td></tr>
              ) : sorted.map((c, i) => (
                <tr key={c.ComplaintID || i}>
                  <td>
                    <span className="cp-rpt-type-cell">
                      <span className="cp-rpt-type-icon">{TYPE_ICON[c.ComplaintText] || '📌'}</span>
                      {c.ComplaintText}
                    </span>
                  </td>
                  <td><span className="cp-rpt-chip">{c.RoomNumber}</span></td>
                  <td>{c.tenantName}</td>
                  <td><PriBadge type={c.ComplaintText} /></td>
                  <td className="cp-rpt-td-mono">{formatDate(c.ComplaintDate)}</td>
                  <td><StatusBadge status={c.ComplaintStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="cp-rpt-tbl-foot">{sorted.length} record{sorted.length !== 1 ? 's' : ''} · {data.length} total</div>
      </div>
    </div>
  )
}

// ── ComplaintsReport root ────────────────────────────────────────────────────

function ComplaintsReport({ selectedPGId, selectedPG, selectedYear }) {
  const [data,    setData]    = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [view,    setView]    = useState('overview')   // overview | trends | all

  useEffect(() => {
    if (!selectedPGId) return
    setLoading(true)
    setError(null)
    getAllComplaintsByPGID(selectedPGId)
      .then(response => {
        const raw = Array.isArray(response) ? response : (response?.data || [])
        // Attach _year/_month for grouping
        const tagged = raw.map(c => ({
          ...c,
          _year:  c.ComplaintDate ? String(new Date(c.ComplaintDate).getFullYear()) : '—',
          _month: c.ComplaintDate ? new Date(c.ComplaintDate).getMonth() : 0,
        }))
        setData(tagged.length > 0 ? tagged : DUMMY_COMPLAINTS)
      })
      .catch(() => setData(DUMMY_COMPLAINTS))   // fall back to dummy while API is not ready
      .finally(() => setLoading(false))
  }, [selectedPGId])

  if (loading) return <div className="rpt-state">Loading complaints data…</div>
  if (error)   return <div className="rpt-state rpt-state-error">Error: {error}</div>

  return (
    <div className="cp-rpt-root">
      <div className="cp-rpt-view-tabs">
        {[
          { key: 'overview', label: 'Overview'       },
          { key: 'trends',   label: 'Monthly trends' },
          { key: 'all',      label: 'All complaints' },
        ].map(v => (
          <button
            key={v.key}
            className={`cp-rpt-vtab${view === v.key ? ' on' : ''}`}
            onClick={() => setView(v.key)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === 'overview' && <CpOverview data={data} selectedYear={selectedYear} />}
      {view === 'trends'   && <CpTrends   data={data} />}
      {view === 'all'      && <CpAllComplaints data={data} />}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT — OwnerReports
// ═══════════════════════════════════════════════════════════════════════════════

const OwnerReports = ({ selectedPG, selectedPGId }) => {
  const today    = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const fmt      = d => d.toISOString().split('T')[0]

  const [dateFrom,      setDateFrom]    = useState(fmt(firstDay))
  const [dateTo,        setDateTo]      = useState(fmt(today))
  const [activeReport,  setActiveReport] = useState('occupancy')
  const [dropOpen,      setDropOpen]    = useState(false)
  const dropRef = useRef(null)

  const currentYear    = today.getFullYear()
  const availableYears = Object.keys(MOCK_MONTHLY).map(Number).sort((a, b) => a - b)
  const defaultYear    = availableYears.includes(currentYear) ? currentYear : availableYears[availableYears.length - 1]
  const [selectedYear, setSelectedYear] = useState(defaultYear)

  useEffect(() => {
    const handler = e => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const activeLabel = REPORT_TYPES.find(r => r.key === activeReport)?.label || 'Select Report'

  return (
    <div className="rpt-root">

      {/* ── Top bar ── */}
      <div className="rpt-topbar">
        <div className="rpt-topbar-left">
          <div className="rpt-date-group">
            <label className="rpt-date-label">From</label>
            <input type="date" className="rpt-date-input" value={dateFrom} max={dateTo} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <span className="rpt-date-sep">→</span>
          <div className="rpt-date-group">
            <label className="rpt-date-label">To</label>
            <input type="date" className="rpt-date-input" value={dateTo} min={dateFrom} max={fmt(today)} onChange={e => setDateTo(e.target.value)} />
          </div>
        </div>

        <div className="rpt-dropdown" ref={dropRef}>
          <button className="rpt-dropdown-btn" onClick={() => setDropOpen(o => !o)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" />
            </svg>
            {activeLabel}
            <svg className={`rpt-chevron ${dropOpen ? 'rpt-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {dropOpen && (
            <div className="rpt-dropdown-menu">
              {REPORT_TYPES.map(r => (
                <button
                  key={r.key}
                  className={`rpt-dropdown-item ${activeReport === r.key ? 'rpt-dropdown-item-active' : ''}`}
                  onClick={() => { setActiveReport(r.key); setDropOpen(false) }}
                >
                  {r.label}
                  {activeReport === r.key && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Header + year pills ── */}
      <div className="rpt-header">
        <div>
          <h2 className="rpt-title">{activeLabel}</h2>
          <p className="rpt-subtitle">
            {selectedPG} · {new Date(dateFrom).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })} – {new Date(dateTo).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
          </p>
        </div>
        <div className="rpt-year-filter">
          {availableYears.slice(-3).map(year => (
            <button key={year} className={`rpt-year-btn ${selectedYear === year ? 'active' : ''}`} onClick={() => setSelectedYear(year)}>
              {year}
            </button>
          ))}
        </div>
      </div>

      {/* ── Active report ── */}
      {activeReport === 'occupancy' && (
        <MonthlyOccupancyReport
          selectedPGId={selectedPGId} selectedPG={selectedPG}
          dateFrom={dateFrom} dateTo={dateTo}
          selectedYear={selectedYear} onYearChange={setSelectedYear}
        />
      )}
      {activeReport === 'complaints' && (
        <ComplaintsReport
          selectedPGId={selectedPGId} selectedPG={selectedPG}
          selectedYear={String(selectedYear)}
        />
      )}
      {activeReport === 'revenue'    && <ComingSoon label="Revenue" />}
      {activeReport === 'compliance' && <ComingSoon label="Compliance" />}

    </div>
  )
}

export default OwnerReports